import React, { Component } from 'react'
import { Icon, message, Spin, Tooltip } from 'antd'
import subscriptionService from '@services/subscriptions'
import { tranzila } from '@services/api'
import StoDetailsModal from '@pages/SearchStoData/StoDetailsModal'
import { stoErrKeyText, stoFailureText } from '@pages/Tranzila/stoErrors'
import moment from 'moment'
import { alertPopup } from '@utils/popup'

interface CLI {
    id: number
    sto_id?: string
    terminal_name?: string
    sto_status?: string
    subscription_id?: string
    subscription_status?: string
    subscription_price?: string
    subscription_paid_at?: string
    expire_date?: string
    created_at?: string
    card_last_digits?: string
    user_subscription_id?: number
    coupon_details?: any
    bank_code?: string
    bank_name?: string
    branch_code?: string
    account_number?: string
    err_key?: string
    parent_id?: number
    prepayment_until?: string
    future_cancellation_date?: string
    open_cancellation_id?: number
}

interface SubscriptionTypeProps {
    loading_sto: boolean
    subscriptions: any[]
    roles: any[]
    updateInfo: () => void
    clientInfo: CLI
}

interface SubscriptionTypeState {
    loading: boolean
    couponTitle: any
    monthsCoupon: boolean
    stoDetailsLoading: boolean
    selectedSto: any | null
}

class SubscriptionType extends Component<SubscriptionTypeProps, SubscriptionTypeState> {
    stoDetailsRequest = 0

    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            couponTitle: null,
            monthsCoupon: false,
            stoDetailsLoading: false,
            selectedSto: null
        }
    }

    couponDetails = ({ discount }) => {
        const today = new Date()
        today.setMonth(today.getMonth() + discount)
        return {
            discount,
            future_date: today.toISOString().split('T')[0]
        }
    }

    bankApproved = async () => {
        const { clientInfo, updateInfo } = this.props
        const { monthsCoupon } = this.state

        const payload = {
            user_id: clientInfo.id,
            user_subscription_id: clientInfo.user_subscription_id
        }
        if (monthsCoupon) payload['coupon_details'] = this.couponDetails(clientInfo.coupon_details)

        this.setState({ loading: true }, async () => {
            await subscriptionService
                .updateBankApprovalStatus(payload)
                .then(() => {
                    this.setState({ loading: false })
                    updateInfo()
                })
                .catch(err => {
                    console.log('bankApproved err::=>', err)
                    // Always reset loading so the control is not stuck on a spinner.
                    this.setState({ loading: false })
                    alertPopup({ type: 'error', title: 'הפעלת המנוי נכשלה או לא בוצעה באופן מלא', body: '' })
                })
        })
    }

    componentDidMount() {
        const {
            clientInfo: { coupon_details }
        } = this.props
        if (coupon_details) {
            const { units, discount } = coupon_details

            if (units === 'MONTHS') {
                const couponTitle = discount > 1 ? `(תשלום עתידי בעוד ${discount} חודשים)` : '(תשלום עתידי בעוד חודש)'
                this.setState({ couponTitle, monthsCoupon: true })
            }
        }
    }

    componentWillUnmount() {
        this.stoDetailsRequest += 1
    }

    openStoDetails = async () => {
        const { sto_id, terminal_name, sto_status } = this.props.clientInfo
        if (String(sto_id) === '0') return
        if (!sto_id || !terminal_name || this.state.stoDetailsLoading) return

        const requestId = ++this.stoDetailsRequest
        this.setState({ stoDetailsLoading: true })

        try {
            const response = await tranzila.getStosList({
                stoId: sto_id,
                terminalName: [terminal_name],
                status: sto_status
            })
            if (requestId !== this.stoDetailsRequest) return

            const rows = Array.isArray(response.data) ? response.data : []
            const selectedSto = rows.find(
                row =>
                    String(row.sto_id) === String(sto_id) &&
                    String(row.terminal_name || '').trim() === String(terminal_name).trim()
            )

            if (!selectedSto) {
                message.error('לא נמצאו פרטי הוראת חיוב')
                return
            }

            this.setState({ selectedSto })
        } catch (error) {
            if (requestId === this.stoDetailsRequest) {
                message.error(stoFailureText(error, 'שגיאה בטעינת פרטי הוראת חיוב'))
            }
        } finally {
            if (requestId === this.stoDetailsRequest) {
                this.setState({ stoDetailsLoading: false })
            }
        }
    }

    getScheduledCancellation = () => {
        const value = this.props.clientInfo.future_cancellation_date
        if (!value) return null
        const date = moment(value, 'YYYY-MM-DD')
        if (!date.isValid()) return null
        return {
            text: date.format('DD/MM/YYYY'),
            overdue: date.isBefore(moment().startOf('day'))
        }
    }

    render() {
        const { clientInfo, subscriptions, roles, loading_sto } = this.props
        const { loading, couponTitle, monthsCoupon, stoDetailsLoading, selectedSto } = this.state
        const scheduledCancellation = this.getScheduledCancellation()

        const {
            card_last_digits,
            subscription_id,
            sto_id,
            terminal_name,
            sto_status,
            created_at,
            subscription_status,
            subscription_price,
            subscription_paid_at,
            expire_date,
            bank_code,
            bank_name,
            branch_code,
            account_number,
            err_key,
            parent_id,
            prepayment_until
        } = clientInfo

        // prettier-ignore
        const paidAtFormatted = moment(subscription_paid_at).format('DD/MM/YYYY').toString()
        const price = '₪' + subscription_price

        // Guard against subscriptions not being loaded yet / a missing match.
        const subscription = (subscriptions || []).find(subcription => subcription.id == subscription_id)
        const subscriptionName = subscription ? subscription.name : 'ללא'

        const checkStatus = ['ACTIVE']
        // prettier-ignore
        const stoNull = subscription_id && checkStatus.includes(subscription_status) && !parent_id && (!sto_id || !terminal_name || !sto_status)
        // prettier-ignore
        const stoExist = subscription_id && checkStatus.includes(subscription_status) && !parent_id && sto_id && terminal_name && sto_status

        const blockedCase = subscription_status === 'BLOCKED' && sto_status === 'active'

        const errByKey = {
            OPPOSITE_STO_DATA: 'שימו לב! יש הו״ק פעילה',
            MULTIPLE_ACTIVE_STO: 'שימו לב! יש מספר הו״ק פעילות',
            FILTER_MISMATCH: 'לא נמצא חיוב תואם, יש לבדוק מול מחלקת הגבייה'
        }

        const itemType = terminal_name === 'mediwhobk' ? 'bank' : 'credit-card'
        const isSyntheticPrepaymentSto = String(sto_id) === '0'

        const error = blockedCase
            ? errByKey['OPPOSITE_STO_DATA']
            : stoErrKeyText(err_key, errByKey, 'אין הו״ק פעילה')

        return (
            <div className="subscription-type-container">
                <StoDetailsModal
                    visible={Boolean(selectedSto)}
                    sto={selectedSto}
                    onClose={() => this.setState({ selectedSto: null })}
                />
                <strong>
                    {loading_sto ? <Spin size="small" style={{ marginLeft: '5px' }} /> : null}

                    {(stoNull || (err_key && subscription_status !== 'BLOCKED') || blockedCase) && !loading_sto ? (
                        <Tooltip placement="top" title={error}>
                            <Icon
                                type="exclamation-circle"
                                theme="twoTone"
                                twoToneColor="red"
                                style={{ marginLeft: '5px', cursor: 'pointer' }}
                            />
                        </Tooltip>
                    ) : null}

                    {stoExist && !loading_sto ? (
                        <Tooltip
                            placement="topRight"
                            title={
                                itemType === 'bank' ? (
                                    <div>
                                        {bank_name ? <div>שם הבנק: {bank_name}</div> : null}
                                        <div>קוד בנק: {bank_code}</div>
                                        <div>מספר סניף: {branch_code}</div>
                                        <div>מספר חשבון: {account_number}</div>
                                    </div>
                                ) : (
                                    <div>
                                        <div>תוקף כרטיס: {expire_date}</div>
                                        <div>ספרות אחרונות: {card_last_digits}</div>
                                    </div>
                                )
                            }
                        >
                            <Icon
                                type={stoDetailsLoading ? 'loading' : itemType}
                                theme={stoDetailsLoading ? undefined : 'twoTone'}
                                // twoToneColor={blockedCase ? 'red' : '#1890ff'}
                                twoToneColor={'#1890ff'}
                                style={{
                                    marginLeft: '5px',
                                    cursor: isSyntheticPrepaymentSto ? 'default' : 'pointer',
                                    color: 'red'
                                }}
                                onClick={isSyntheticPrepaymentSto ? undefined : this.openStoDetails}
                            />
                        </Tooltip>
                    ) : null}
                    <span>{'סוג מנוי:'}</span>
                </strong>

                {created_at && subscription_status !== 'AWAITING_BANK_APPROVAL' ? (
                    subscription ? (
                        <Tooltip
                            title={
                                <div>
                                    <div>מחיר: {price}</div>
                                    <div>נרכש בתאריך: {paidAtFormatted}</div>
                                    {prepayment_until && (
                                        <div>תשלום מראש עד: {moment(prepayment_until).format('DD/MM/YYYY')}</div>
                                    )}
                                </div>
                            }
                        >
                            <span
                                style={{
                                    marginRight: '5px',
                                    color: stoNull && !loading_sto ? 'red' : ''
                                }}
                                className="subscription-type"
                            >
                                {subscriptionName}
                            </span>
                        </Tooltip>
                    ) : (
                        'ללא'
                    )
                ) : clientInfo['subscription_status'] === 'AWAITING_BANK_APPROVAL' ? (
                    roles.includes('admin') ||
                    roles.includes('money') ||
                    (roles.includes('distributors_manager') && roles.includes('editor')) ? (
                        !loading ? (
                            <a title={subscription_price + '₪ - ' + subscriptionName} onClick={this.bankApproved}>
                                <strong style={{ color: 'red' }}> ממתין לאישור בנק</strong>
                                {monthsCoupon ? (
                                    <span style={{ marginRight: '5px', fontSize: '12px' }}>{couponTitle}</span>
                                ) : (
                                    ''
                                )}
                            </a>
                        ) : (
                            <Spin style={{ marginRight: '23%' }} />
                        )
                    ) : (
                        <span>
                            <strong style={{ color: 'red' }}> ממתין לאישור בנק</strong>
                            {monthsCoupon ? (
                                <span style={{ marginRight: '5px', fontSize: '12px' }}>{couponTitle}</span>
                            ) : (
                                ''
                            )}
                        </span>
                    )
                ) : (
                    ''
                )}
                {scheduledCancellation &&
                    (scheduledCancellation.overdue ? (
                        <div style={{ marginTop: '4px' }}>
                            <Icon
                                type="exclamation-circle"
                                theme="twoTone"
                                twoToneColor="red"
                                style={{ marginLeft: '5px' }}
                            />
                            {clientInfo.open_cancellation_id ? (
                                <a
                                    href={`/cancelSubRequests/${clientInfo.open_cancellation_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit' }}
                                >
                                    <strong
                                        style={{ color: 'red' }}
                                    >{`המנוי היה אמור להתבטל בתאריך ${scheduledCancellation.text}`}</strong>
                                </a>
                            ) : (
                                <strong
                                    style={{ color: 'red' }}
                                >{`המנוי היה אמור להתבטל בתאריך ${scheduledCancellation.text}`}</strong>
                            )}
                        </div>
                    ) : (
                        <div style={{ marginTop: '4px' }}>
                            {clientInfo.open_cancellation_id ? (
                                <a
                                    href={`/cancelSubRequests/${clientInfo.open_cancellation_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'inherit' }}
                                >
                                    <strong
                                        style={{ color: '#fa8c16' }}
                                    >{`המנוי יבוטל בתאריך ${scheduledCancellation.text}`}</strong>
                                </a>
                            ) : (
                                <strong
                                    style={{ color: '#fa8c16' }}
                                >{`המנוי יבוטל בתאריך ${scheduledCancellation.text}`}</strong>
                            )}
                        </div>
                    ))}
            </div>
        )
    }
}

export default SubscriptionType
